import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { trackEvent } from "@/lib/analytics";
import { useIsMobile, reveal } from "@/lib/motion";
import { GLASS, GLASS_CSS, PHI, TYPE, GLOW, glassReveal } from "@/lib/glass";
import WaitlistSuccess from "@/components/WaitlistSuccess";
import Seal from "@/components/home/Seal";
import { useIgnite } from "@/lib/ignite";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Waitlist() {
  const mobile = useIsMobile();
  const [enterRef, enterLit] = useIgnite();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("form");
  const [enteredAt, setEnteredAt] = useState(0);

  const stateRef = useRef({ email: "", phase: "form" });
  stateRef.current = { email, phase };

  useEffect(() => {
    const handler = () => {
      const s = stateRef.current;
      if (s.phase === "form" && s.email.trim()) trackEvent("form_abandonment");
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Please enter your email to proceed.");
      trackEvent("form_abandonment", "empty_submit");
      return;
    }
    setError("");
    setBusy(true);
    try {
      const res = await fetch(`${API}/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(typeof d.detail === "string" ? d.detail : "request failed");
      }
      trackEvent("email_capture");
      setEnteredAt(Date.now());
      setPhase("success");
    } catch (err) {
      setError(err?.message && err.message !== "request failed" ? err.message : "The light is momentarily unreachable. Please try once more.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      id="waitlist"
      data-testid="waitlist-section"
      data-room="warm"
      className="room room--warm"
      style={{ paddingLeft: PHI.s3, paddingRight: PHI.s3, paddingTop: PHI.s6, paddingBottom: PHI.s6 }}
    >
      <div className="mx-auto w-full max-w-[37rem]">
        <p data-testid="waitlist-eyebrow" className="type-eyebrow text-center" style={{ marginBottom: PHI.s3 }}>The foundational document · by invitation</p>
        <motion.h2
          ref={enterRef}
          data-testid="waitlist-heading"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          {...glassReveal(reveal(mobile))}
          style={{ marginBottom: PHI.s4, fontSize: TYPE.h2, letterSpacing: "0.02em", lineHeight: 1.1, textShadow: GLOW.silver }}
          className={`ignite text-center font-display font-light text-[#F5F5F7]${enterLit ? " ignite--lit" : ""}`}
        >
          Enter the Light
        </motion.h2>
        <motion.p
          data-testid="waitlist-subheading"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 0.8, y: 0 }}
          {...glassReveal(reveal(mobile, { delay: 0.12 }))}
          style={{ marginBottom: PHI.s4, fontSize: TYPE.body, lineHeight: 1.85, letterSpacing: "0.02em", textShadow: GLOW.silverSoft }}
          className="text-center font-light text-[#F5F5F7]"
        >
          Join the earliest circle of creators. Receive the foundational document
          for Prism Pixel™—the first words on this new medium. Early access.
          Direct invitation.
        </motion.p>

        <div className="min-h-[220px]">
          <AnimatePresence mode="wait">
            {phase === "form" ? (
              <motion.form
                key="form"
                data-testid="waitlist-form"
                onSubmit={submit}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.9, ease: GLASS.settle }}
                className="flex w-full flex-col"
                style={{ gap: PHI.s2 }}
                noValidate
              >
                <label htmlFor="waitlist-email" className="sr-only">
                  Email address
                </label>
                <input
                  id="waitlist-email"
                  data-testid="waitlist-email-input"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError("");
                  }}
                  placeholder="Enter email address"
                  aria-invalid={error ? "true" : undefined}
                  aria-describedby={error ? "waitlist-error" : undefined}
                  style={{
                    fontSize: TYPE.body,
                    letterSpacing: "0.02em",
                    transitionTimingFunction: GLASS_CSS.refract,
                    transitionDuration: "460ms",
                    boxShadow: GLOW.suspend,
                  }}
                  className="min-h-[52px] w-full border border-[#F5F5F7] bg-[#030305]/40 p-4 font-light text-[#F5F5F7] opacity-80 transition-[border-color,background-color,box-shadow,opacity] placeholder:text-sm placeholder:font-light placeholder:text-[#F5F5F7]/60 focus:border-[#84DFF4] focus:opacity-100 focus:shadow-[0_26px_70px_-30px_rgba(0,0,0,0.9),0_0_48px_-14px_rgba(132,223,244,0.28)]"
                />
                {error && (
                  <p
                    id="waitlist-error"
                    data-testid="waitlist-error"
                    role="alert"
                    style={{ fontSize: TYPE.small, letterSpacing: "0.02em" }}
                    className="font-light text-[#84DFF4]"
                  >
                    {error}
                  </p>
                )}
                <button
                  data-testid="waitlist-submit-button"
                  type="submit"
                  disabled={busy}
                  style={{
                    fontSize: TYPE.body,
                    letterSpacing: "0.05em",
                    transitionTimingFunction: GLASS_CSS.refract,
                    transitionDuration: "480ms",
                  }}
                  className="min-h-[52px] cursor-pointer border border-[#F5F5F7] bg-transparent px-10 py-4 font-light text-[#F5F5F7] transition-[color,background-color,border-color,box-shadow] hover:border-[#84DFF4] hover:bg-[#84DFF4] hover:text-[#030305] hover:shadow-[0_0_44px_-6px_rgba(132,223,244,0.5)] active:opacity-85 disabled:cursor-wait disabled:opacity-50"
                >
                  {busy ? "Entering…" : "Enter"}
                </button>
              </motion.form>
            ) : (
              <WaitlistSuccess key="success" email={email.trim()} enteredAt={enteredAt} />
            )}
          </AnimatePresence>
        </div>
        <Seal variant="lozenge" testId="waitlist-seal" style={{ marginTop: PHI.s5 }} />
      </div>
    </section>
  );
}
